// Перечисление устройств.
//
// Порядок здесь не вопрос вкуса: до выдачи разрешения `enumerateDevices`
// возвращает устройства **без имён**, и списки получились бы из пустых строк.
// Поэтому сначала `getUserMedia`, и только потом перечисление — причём при
// живом потоке: в Firefox после `track.stop()` имена снова исчезают.

export interface DeviceOption {
  deviceId: string;
  label: string;
}

export interface DeviceLists {
  inputs: DeviceOption[];
  outputs: DeviceOption[];
}

/** Ограничения захвата. Все три обработки — выключены, и это обязательно. */
export function captureConstraints(deviceId: string | null): MediaStreamConstraints {
  return {
    audio: {
      // Браузер по умолчанию включает эхоподавление, шумодав и авторегулировку
      // усиления. На вход RVC пришёл бы уже испорченный сигнал, и понять это на
      // слух почти невозможно — искать стали бы в модели.
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    },
    video: false,
  };
}

/**
 * Настоящая частота дорожки — не та, что у контекста.
 *
 * Контекст мы создаём сами и его частоту знаем; дорожка может прийти какой
 * угодно, и браузер молча приведёт её к контексту. Bluetooth-гарнитура,
 * выбранная микрофоном, уходит в гарнитурный профиль и отдаёт 8–16 кГц:
 * растянутый до 48 сигнал звучит глухо, потому что верхней половины спектра в
 * нём просто нет. Ни один счётчик об этом не скажет — отсюда и функция.
 *
 * `null` означает «браузер не сообщил», а не «всё хорошо»: `sampleRate` в
 * `getSettings()` есть не везде.
 */
export function trackSampleRate(track: MediaStreamTrack): number | null {
  const rate = track.getSettings().sampleRate;
  return typeof rate === "number" && rate > 0 ? rate : null;
}

/**
 * Что браузер на самом деле сделал со звуком.
 *
 * Просить — не значит получить: настройки платформозависимы. Расхождение надо
 * показать в интерфейсе, иначе искать его будут спектрограммой.
 */
export function unwantedProcessing(track: MediaStreamTrack): string[] {
  const settings = track.getSettings() as MediaTrackSettings & {
    echoCancellation?: boolean;
    noiseSuppression?: boolean;
    autoGainControl?: boolean;
  };

  const enabled: string[] = [];
  if (settings.echoCancellation) enabled.push("эхоподавление");
  if (settings.noiseSuppression) enabled.push("шумодав");
  if (settings.autoGainControl) enabled.push("авторегулировка усиления");
  return enabled;
}

/** Перечислить устройства. Вызывать только при живом потоке. */
export async function listDevices(): Promise<DeviceLists> {
  const devices = await navigator.mediaDevices.enumerateDevices();

  const pick = (kind: MediaDeviceKind, fallback: string): DeviceOption[] =>
    devices
      .filter((device) => device.kind === kind)
      .map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label !== "" ? device.label : `${fallback} ${index + 1}`,
      }));

  return {
    inputs: pick("audioinput", "Микрофон"),
    outputs: pick("audiooutput", "Выход"),
  };
}
